import type { ValidationIssue } from "@nexxt/contracts";
import type { ClaimOutcome, ClaimStatus, ClaimsIssueRule, ClaimType, TicketRequester } from "@nexxt/contracts/claims";
import { m } from "../../../paraglide/messages.js";
import type { SalesOrderLine } from "../sales-order-line/line.queries";

// The words of a claim for the Customer Service (ADR-225, ADR-228): its kind, where its ticket stands on Zendesk, what
// was decided there, and why the domain refused one.

export const claimTypeLabel: Record<ClaimType, () => string> = {
  missing_parcels: m.claim_type_missing_parcels,
  defective: m.claim_type_defective,
  faulty: m.claim_type_faulty,
  other: m.claim_type_other,
};

/** `open` until Zendesk has its ticket (I-17), then what the ticket says (I-32). */
export const claimStatusLabel: Record<ClaimStatus, () => string> = {
  open: m.claim_status_open,
  submitted: m.claim_status_submitted,
  in_progress: m.claim_status_in_progress,
  resolved: m.claim_status_resolved,
  closed: m.claim_status_closed,
};

export const claimOutcomeLabel: Record<ClaimOutcome, () => string> = {
  accepted: m.claim_outcome_accepted,
  rejected: m.claim_outcome_rejected,
  replacement: m.claim_outcome_replacement,
  credit_note: m.claim_outcome_credit_note,
};

/** Who Zendesk sends the answers of the ticket to (B-78, ADR-252). */
export const ticketRequesterLabel: Record<TicketRequester, () => string> = {
  customer: m.claim_requester_customer,
  order_buyer: m.claim_requester_order_buyer,
  buyer_contact: m.claim_requester_buyer_contact,
  customer_service: m.claim_requester_customer_service,
};

/** "Faulty product · SO-2026-003412": the claim by its kind and its order. */
export const claimName = (claim: { type: ClaimType; salesOrder: { soNumber: string } }): string =>
  m.claim_name({ type: claimTypeLabel[claim.type](), order: claim.salesOrder.soNumber });

/** The ticket could not be opened and has none: the Customer Service sends it again (the rule of the domain). */
export const ticketFailed = (claim: { ticketId: string | null; ticketError?: string | null | undefined }): boolean =>
  claim.ticketId === null && claim.ticketError != null;

const issueTexts: Record<ClaimsIssueRule, () => string> = {
  photos_required: m.claim_issue_photos_required,
  duplicate_file: m.claim_issue_duplicate_file,
  unknown_file: m.claim_issue_unknown_file,
  wrong_folder: m.claim_issue_wrong_folder,
  unknown_line: m.claim_issue_unknown_line,
  quantity_without_line: m.claim_issue_quantity_without_line,
};

/** An issue of a refused claim, in the words of the Customer Service. */
export function claimIssueText(issue: ValidationIssue): string {
  const text = issueTexts[issue.rule as ClaimsIssueRule] as (() => string) | undefined;
  return text ? text() : m.claim_issue_other({ field: issue.field, rule: issue.rule });
}

/** A line as the Customer Service finds it: number, product, colour, size and SKU. */
export function lineText(line: SalesOrderLine): string {
  return [`#${line.lineNo}`, line.styleName, line.colour, line.size, line.skuCode].filter(Boolean).join(" · ");
}
